<template>
  <div>Envía mensajes de texto SMS al contacto, sin la opción de respuesta.</div>
  <p class="campaign_name">Estás en la campaña: {{ sequence?.portfolio?.name }}</p>
  <a-form layout="vertical" ref="formRef" :model="formState" :rules="rules">
    <p class="title">Configura tu nodo</p>
    <queue-picker
      :name="'queue'"
      :value="formState.queue"
      :portfolio="sequence?.portfolio?.id"
      :showAll="false"
      :showTooltip="false"
      :disabled="disabled"
      @update:value="handleSelectQueue"
      @change="changeDisabledButton"
      required
    >
      <template #label>
        <span>Cola</span>
      </template>
    </queue-picker>
    <a-form-item name="message_type" required>
      <template #label>
        <div>
          <span>Tipo de mensaje</span>
        </div>
      </template>
      <a-select
        @change="changeMessageType"
        v-model:value="formState.message_type"
        :getPopupContainer="(triggerNode: any) => triggerNode.parentNode"
        placeholder="Seleccionar tipo de mensaje"
        :disabled="disabled"
      >
        <a-select-option v-for="typeMessage in typesMessage" :key="typeMessage.value">
          {{ typeMessage.label }}
        </a-select-option>
      </a-select>
    </a-form-item>
    <div class="template_sms">
      <!-- template sms -->
      <a-form-item
        name="sms_template"
        required
        v-if="formState.message_type === SequenceTypeMessage.UseTemplate"
      >
        <SmsTemplatePicker
          :type="formState.message_type"
          :select-label="undefined"
          :queue="formState.queue"
          :disabled="disabled"
          @update:templateId="
            formState.sms_template = $event;
            changeDisabledButton();
          "
          @update:template="smsTemplate = $event"
          @update:templateItem="smsTemplateItem = $event"
          :template="formState.sms_template || undefined"
          allParameters
        >
          <template #label>
            Plantilla de SMS
            <a-button
              :disabled="!formState.sms_template || disabled"
              type="link"
              @click="showModal = true"
            >
              Previsualiza tu plantilla
            </a-button>
          </template>
        </SmsTemplatePicker>
        <div v-if="smsTemplateItem?.parameters?.length > 0">
          <template-parameters
            v-model:values="formState.parameters"
            :template="smsTemplateItem"
            :portfolioId="sequence?.portfolio?.id"
            :disabled="disabled"
          />
        </div>
      </a-form-item>
      <!-- redact sms -->
      <div style="width: 100%" v-if="formState.message_type === SequenceTypeMessage.ComposeMessage">
        <a-form-item required name="sms_message" label="Mensaje del SMS" style="width: 100%">
          <a-button class="btn-sms" primary @click="openMessageSms = true" :disabled="disabled"
            >Agregar mensaje del SMS</a-button
          >
        </a-form-item>
        <a-modal
          v-model:open="openMessageSms"
          width="800px"
          class="redact_sms"
          :title="`Redacta el mensaje de tu SMS`"
        >
          <div style="display: flex; width: 100%">
            <!-- @valid="handleChangeInput($event)" -->
            <SmsTemplatePicker
              :type="formState.message_type"
              select-label="Plantilla de SMS"
              :queue="formState.queue"
              :message="formState.sms_message || undefined"
              @update:message="formState.sms_message = $event"
            >
              <template #preview-button>
                <a-popover arrowPointAtCenter placement="right">
                  <template #content>
                    <SmsTemplatePhone v-if="formState.sms_template" :content="smsTemplate" />
                    <span v-else>
                      Selecciona una plantilla para
                      <br />
                      poder previsualizar su contenido.
                    </span>
                  </template>

                  <a-button :disabled="!formState.sms_template" type="link">
                    Previsualiza tu plantilla
                  </a-button>
                </a-popover>
              </template>
            </SmsTemplatePicker>

            <SmsTemplatePhone
              style="margin-bottom: 16px; margin-left: 40px"
              v-if="formState.message_type === 'write'"
              :content="formState.sms_message"
              preview="Visualiza el contenido de tu mensaje SMS aquí."
            />
          </div>
          <template #footer>
            <a-button key="back" @click="openMessageSms = false">Cancelar</a-button>
            <a-button key="confirm" type="primary" @click="openMessageSms = false"
              >Confirmar cambios</a-button
            >
          </template>
        </a-modal>
      </div>
    </div>
    <p class="title">Defina el siguiente paso</p>
    <ActionPicker
      v-if="formState.action"
      v-model:value="formState.action"
      :disabled="disabled"
      :isMultichannel="true"
      :sequence="sequence"
      @update:value="changeDisabledButton"
    />
  </a-form>

  <a-divider class="divider" type="horizontal" />
  <h3>Redirección cuando el contacto conteste</h3>
  <p style="color: #00000073">
    El identificador asociado a esta secuencia está configurado específicamente para enviar mensajes
    de texto (SMS), mas no de recibir respuestas. Por lo tanto, la secuencia podrá avanzar sin
    esperar una respuesta del contacto.
  </p>

  <a-modal
    title="Plantilla de SMS"
    class="modal-template"
    v-model:open="showModal"
    width="500px"
    :bodyStyle="{ height: '500px', justifyContent: 'center', display: 'flex' }"
    centered
  >
    <div class="container-template">
      <SmsTemplatePhone v-if="formState.sms_template" :content="smsTemplate" />
    </div>
    <template #footer>
      <a-divider />
      <a-button @click="showModal = false" class="btn-large"> Cerrar previsualización </a-button>
    </template>
  </a-modal>
</template>

<script setup lang="ts">
import { ref, onMounted, inject, Ref, watch } from "vue";
import { FormInstance } from "ant-design-vue";

import { Sequence, SequenceNode, SequenceTypeMessage } from "@/@types/flows/sequences";
import TemplateParameters from "@/app/flows/sequences/components/sections/drawerForms/TemplateParameters.vue";
import ActionPicker from "@/app/flows/sequences/components/pickers/ActionPicker.vue";
import { WppTemplate } from "@/@types/global";
import { fetchTemplates } from "@/app/workspace/services";
import QueuePicker from "@/app/shared/components/pickers/QueuePicker.vue";
import SmsTemplatePicker from "@/app/strategies/components/pickers/SmsTemplatePicker.vue";
import SmsTemplatePhone from "@/app/channels/components/modals/sms/templates/SmsTemplatePhone.vue";

const props = defineProps<{ node: any; disabled: boolean; sequence: Sequence }>();

const formRef = ref<FormInstance>();
const formState = ref<Partial<SequenceNode>>({
  action: props.node.action,
  queue: props.node?.queue?.id,
  message_type: props.node?.sms_message
    ? SequenceTypeMessage.ComposeMessage
    : props.node?.sms_template
    ? SequenceTypeMessage.UseTemplate
    : null,
  sms_message: props.node?.sms_message || undefined,
  sms_template: props.node?.sms_template,
  parameters: props.node?.parameters ?? [],
});

const rules = {
  queue: [{ required: true, message: "Campo requerido" }],
  message_type: [{ required: true, message: "Campo requerido" }],
  sms_message: [{ required: true, message: "Campo requerido" }],
  sms_template: [{ required: true, message: "Campo requerido" }],
  parameters: [{ required: true }],
};

watch(
  () => props.node,
  () => {
    formState.value = {
      waiting_time: props.node.waiting_time,
      action: props.node.action,
      queue: props.node?.queue?.id,
      message_type: props.node?.sms_message
        ? SequenceTypeMessage.ComposeMessage
        : props.node?.sms_template
        ? SequenceTypeMessage.UseTemplate
        : null,
      sms_message: props.node?.sms_message || undefined,
      sms_template: props.node?.sms_template,
      parameters: props.node?.parameters ?? [],
    };
  }
);

const smsTemplate = ref();
const smsTemplateItem = ref();

watch(
  () => smsTemplateItem.value,
  (value) => {
    if (!value?.parameters || value?.parameters?.length == 0) {
      formState.value.parameters = undefined;
    }
  }
);

const typesMessage = ref([
  { value: SequenceTypeMessage.UseTemplate, label: "Usar plantilla" },
  { value: SequenceTypeMessage.ComposeMessage, label: "Redactar mensaje" },
]);
const saveDisabled = inject("saveDisabled") as Ref<Boolean>;

const openMessageSms = ref(false);

const showModal = ref(false);

const getValidatedFormState = async () => {
  await formRef.value?.validateFields();

  return formState.value;
};

const handleSelectQueue = async (value: number) => {
  formState.value.queue = value;
  // await fetchTemplatesWhatsapp();
};

const changeMessageType = (value: SequenceTypeMessage) => {
  if (value == SequenceTypeMessage.UseTemplate) formState.value.sms_message = null;
  else formState.value.sms_template = null;
  changeDisabledButton();
};

const changeDisabledButton = () => {
  saveDisabled.value = false;
};

watch(
  () => formState.value,
  (lastValue, newValue) => {
    if (lastValue && newValue) {
      saveDisabled.value = false;
    }
  },
  { deep: true, immediate: true }
);

defineExpose({ getValidatedFormState });

onMounted(async () => {
  // currentTemplate.value = templatesWhatsapp.value.find(
  //   (template) => template.id === formState.template
  // );
});
</script>

<style scoped lang="sass">
p
  color: $text-primary
  margin-bottom: 24px

h2
  color: $text-primary
  font-size: 16px
  font-weight: 600
  margin-bottom: 12px

.picker
  width: 100%

:deep(.ant-form-item-label > label)
  width: 100%
:deep(.ant-form-item-label > label > div)
  display: flex
  justify-content: space-between
  align-items: center
  width: 100%

.btn-sms
  border: 1px solid #1677ff
  color: #1677ff
  width: 100%

.redact_sms
  display: flex

.container-template
  margin-top: 20px

.btn-large
  width: 350px

.template_sms
  display: flex
  gap: 36px
  align-items: flex-start
  width: 100%

:global(.template_sms .ant-form-item .ant-form-item)
  margin-bottom: 0
.campaign_name
  margin-top: 8px
  margin-bottom: 16px
  font-size: 13px
  font-style: italic
  color: #1677FF
.title
  font-size: 15px
  font-weight: bold
.text-whatsapp-template
  color: $gray-7
</style>
